"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  /**
   * "header": アイコン → クリックで入力欄を展開（ヘッダー用・既定）
   * "inline": 常時表示の大きめ入力欄（検索ページ用）
   */
  variant?: "header" | "inline";
  defaultValue?: string;
  className?: string;
  /** inline 用プレースホルダ上書き */
  placeholder?: string;
};

export default function SearchBox({
  variant = "header",
  defaultValue = "",
  className,
  placeholder,
}: Props) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = value.trim();
    if (!q) {
      inputRef.current?.focus();
      return;
    }
    router.push(`/search?q=${encodeURIComponent(q)}`);
    if (variant === "header") setOpen(false);
  };

  // ---- インライン（検索ページ用・大きめ） ----
  if (variant === "inline") {
    return (
      <form
        onSubmit={submit}
        role="search"
        className={cn("relative", className)}
      >
        <Search
          className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-navy-300"
          aria-hidden
        />
        <input
          ref={inputRef}
          type="search"
          name="q"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder ?? "記事・求人を検索"}
          aria-label="サイト内検索"
          className="w-full rounded-full border border-line bg-white py-4 pl-14 pr-28 text-[15px] text-ink outline-none transition-colors placeholder:text-navy-200 focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
        />
        <button
          type="submit"
          className="btn-primary absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2.5"
        >
          <span className="font-en tracking-[0.06em]">検索</span>
        </button>
      </form>
    );
  }

  // ---- ヘッダー（アイコン展開） ----
  return (
    <div className={cn("relative flex items-center", className)}>
      {open ? (
        <form
          onSubmit={submit}
          role="search"
          className="flex items-center gap-1 rounded-full border border-line bg-white pl-3 pr-1 shadow-sm"
        >
          <Search className="h-4 w-4 shrink-0 text-navy-300" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            name="q"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => {
              if (!value.trim()) setOpen(false);
            }}
            placeholder="検索"
            aria-label="サイト内検索"
            className="w-36 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-navy-200 md:w-44"
          />
          <button
            type="button"
            onClick={() => {
              setValue("");
              setOpen(false);
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-navy-300 transition-colors hover:bg-navy-50 hover:text-navy-700"
            aria-label="検索を閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-navy-900 transition-colors hover:bg-navy-50"
          aria-label="サイト内検索を開く"
        >
          <Search className="h-5 w-5" aria-hidden />
        </button>
      )}
    </div>
  );
}
